import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Play, X } from 'lucide-react';

export const VideoShowcase = () => {
  const { isRTL } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  
  // YouTube video ID from the shorts URL
  const videoId = 'uU94C_r9zho';
  
  // Thumbnail URL - using different formats for better availability
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <section className="py-8 md:py-12 px-4 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-secondary/30 -z-10" />
      
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <h2 className="text-2xl sm:text-3xl font-display text-foreground mb-2">
            {isRTL ? 'צפו בציפוי מינרלי בפעולה' : 'Watch Mineral Coating in Action'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isRTL ? 'לחצו להפעלת הסרטון' : 'Click to play the video'}
          </p>
        </motion.div>

        {/* Video Container - Floating Card Style */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative mx-auto max-w-md"
        >
          {/* Decorative glow behind the video */}
          <motion.div
            className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-accent/30 via-primary/20 to-accent/30 blur-2xl opacity-60"
            animate={{ 
              scale: [1, 1.02, 1],
              opacity: [0.4, 0.6, 0.4]
            }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          
          {/* Video Card */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/50 bg-card">
            {/* Aspect ratio container for vertical video (9:16) */}
            <div className="relative aspect-[9/16]">
              {!isPlaying ? (
                /* Thumbnail with Play Button */
                <motion.button
                  onClick={() => setIsPlaying(true)}
                  className="absolute inset-0 group cursor-pointer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="video-play-btn"
                >
                  {/* Thumbnail Image */}
                  <img
                    src={thumbnailUrl}
                    alt={isRTL ? 'תצוגה מקדימה של הסרטון' : 'Video Preview'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback if thumbnail doesn't load
                      e.target.src = `https://img.youtube.com/vi/${videoId}/0.jpg`;
                    }}
                  />
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  
                  {/* Play Button */}
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ opacity: 0.9 }}
                    whileHover={{ opacity: 1 }}
                  >
                    <motion.div
                      className="w-20 h-20 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                      animate={{ 
                        boxShadow: [
                          '0 0 0 0 rgba(255,255,255,0.4)',
                          '0 0 0 20px rgba(255,255,255,0)',
                          '0 0 0 0 rgba(255,255,255,0)'
                        ]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Play className="w-8 h-8 text-primary fill-primary ms-1" />
                    </motion.div>
                  </motion.div>
                  
                  {/* Bottom label */}
                  <div className="absolute bottom-4 inset-x-4 text-center">
                    <span className="inline-block px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm text-white text-sm font-medium">
                      {isRTL ? 'הפעל סרטון' : 'Play Video'}
                    </span>
                  </div>
                </motion.button>
              ) : (
                /* Embedded YouTube Player - using shorts embed format */
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&loop=1&playlist=${videoId}`}
                  title="Microlit Mineral Coating Video"
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="video-iframe"
                />
              )}
            </div>
          </div>
          
          {/* Close button when playing */}
          <AnimatePresence>
            {isPlaying && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                onClick={() => setIsPlaying(false)}
                className="absolute -top-3 -end-3 w-10 h-10 rounded-full bg-destructive text-white shadow-lg flex items-center justify-center hover:bg-destructive/90 transition-colors z-10"
                data-testid="video-close-btn"
              >
                <X className="w-5 h-5" />
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
